/**
 * ReputationPage — the "Reputation" surface (reskin of the reviews page).
 *
 * Renders the design handoff Reputation layout wired to REAL review-intelligence
 * data (useReviewsPageData): score-ring hero + per-platform ratings, category
 * ratings, an honest momentum readout, themes (loves / watch-outs), the top
 * watch-out as an actionable callout, and recent reviews with channel filters.
 *
 * Honesty per the redesign reconciliation:
 *  - No per-star distribution data exists → replaced with real Category ratings.
 *  - No monthly score history exists → the "6-month trend" is replaced with a
 *    real Momentum readout (sentiment trend + velocity), not a fabricated curve,
 *    and the hero shows the trend direction (no invented "+N this month").
 *  - Draft-response/post-reply and watch-out→task are net-new backend → the
 *    watch-out routes to the relevant fix surface instead of a fake task flip.
 *
 * Ingestion, source health, recompute, upgrade wall and the AI ritual are all
 * preserved by embedding the full ReviewsPage for gated/loading/empty states and
 * via the "Manage sources" toggle.
 */

import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router"
import { Star, TrendingUp, TrendingDown, Minus, ArrowRight, Settings2, ArrowLeft, MessageSquare, Check, Plus, Sparkles, Copy, Loader2 } from "lucide-react"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { usePlanContext } from "@/lib/usePlanContext"
import { apiClient } from "@/lib/api"
import { cn } from "@/lib/utils"
import { ReviewsPage } from "@/pages/ReviewsPage"
import { useTasks } from "@/pages/tasks/useTasks"
import { useReviewsPageData, momentumSummary, themeDisplayName, formatTimeAgo } from "@/pages/reviews/useReviewsData"

const CATEGORY_LABELS: Record<string, string> = {
  accuracy: "Accuracy",
  check_in: "Check-in",
  cleanliness: "Cleanliness",
  communication: "Communication",
  location: "Location",
  value: "Value",
}

function scoreTitle(score: number): string {
  if (score >= 90) return "Excellent reputation"
  if (score >= 78) return "Strong reputation"
  if (score >= 62) return "Solid reputation"
  return "Building reputation"
}

interface MergedReview {
  id: string
  name: string
  initial: string
  src: "Airbnb" | "Booking"
  srcColor: string
  rating: number
  scale: 5 | 10
  text: string
  ago: string
  ts: number
  rawId: string
  sourceKey: "airbnb" | "booking"
}

interface ReplyState {
  status: "draft" | "approved"
  text: string
}

export function ReputationPage() {
  const { propertyId, property } = usePropertyContext()
  const { plan } = usePlanContext()
  const navigate = useNavigate()
  const data = useReviewsPageData(propertyId)
  const { tasks, addTask } = useTasks()
  const [managing, setManaging] = useState(false)
  const [filter, setFilter] = useState<"all" | "Airbnb" | "Booking">("all")

  // Review-reply loop state: draft → edit → approve (human gate) → copy.
  const [replies, setReplies] = useState<Map<string, ReplyState>>(new Map())
  const [openEditor, setOpenEditor] = useState<string | null>(null)
  const [editorText, setEditorText] = useState("")
  const [draftingId, setDraftingId] = useState<string | null>(null)
  const [approvingId, setApprovingId] = useState<string | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  useEffect(() => {
    if (!propertyId) return
    let cancelled = false
    apiClient.getReviewReplies(propertyId).then((res) => {
      if (cancelled || !res.ok || !Array.isArray(res.replies)) return
      const next = new Map<string, ReplyState>()
      for (const r of res.replies) {
        next.set(`${r.source}:${r.review_id}`, {
          status: r.status === "approved" ? "approved" : "draft",
          text: r.final_text || r.draft_text || "",
        })
      }
      setReplies(next)
    }).catch(() => {})
    return () => {
      cancelled = true
    }
  }, [propertyId])

  const analysis = data.intel?.analysis
  const hasIntel = Boolean(data.intel?.available && analysis)

  const merged = useMemo<MergedReview[]>(() => {
    const out: MergedReview[] = []
    for (const r of data.reviews || []) {
      out.push({
        id: `a-${r.id}`,
        name: r.reviewer_name || "Guest",
        initial: (r.reviewer_name || "G").charAt(0).toUpperCase(),
        src: "Airbnb",
        srcColor: "var(--sf-airbnb)",
        rating: r.rating,
        scale: 5,
        text: r.text || r.localized_text || "",
        ago: formatTimeAgo(r.created_at),
        ts: new Date(r.created_at).getTime() || 0,
        rawId: r.id,
        sourceKey: "airbnb",
      })
    }
    for (const r of data.bookingReviews || []) {
      const liked = r.liked_text ? r.liked_text : ""
      const disliked = r.disliked_text ? ` Could improve: ${r.disliked_text}` : ""
      out.push({
        id: `b-${r.id}`,
        name: r.reviewer_name || "Guest",
        initial: (r.reviewer_name || "G").charAt(0).toUpperCase(),
        src: "Booking",
        srcColor: "var(--sf-booking)",
        rating: r.rating,
        scale: 10,
        text: (liked + disliked).trim() || r.review_title || "",
        ago: formatTimeAgo(r.created_at),
        ts: new Date(r.created_at).getTime() || 0,
        rawId: r.id,
        sourceKey: "booking",
      })
    }
    return out.sort((a, b) => b.ts - a.ts)
  }, [data.reviews, data.bookingReviews])

  const replyKey = (rv: MergedReview) => `${rv.sourceKey}:${rv.rawId}`

  const startDraft = async (rv: MergedReview) => {
    if (!propertyId || draftingId) return
    const key = replyKey(rv)
    const existing = replies.get(key)
    if (existing?.text) {
      setOpenEditor(key)
      setEditorText(existing.text)
      return
    }
    setDraftingId(key)
    try {
      const res = await apiClient.draftReviewReply(propertyId, rv.sourceKey, rv.rawId)
      if (res.ok && res.draft) {
        setReplies((prev) => new Map(prev).set(key, { status: "draft", text: res.draft! }))
        setOpenEditor(key)
        setEditorText(res.draft)
      }
    } catch { /* leave state unchanged */ } finally {
      setDraftingId(null)
    }
  }

  const approveReply = async (rv: MergedReview) => {
    if (!propertyId || approvingId) return
    const key = replyKey(rv)
    const finalText = editorText.trim()
    if (!finalText) return
    setApprovingId(key)
    try {
      const res = await apiClient.approveReviewReply(propertyId, rv.sourceKey, rv.rawId, finalText)
      if (res.ok) {
        setReplies((prev) => new Map(prev).set(key, { status: "approved", text: finalText }))
        setOpenEditor(null)
      }
    } catch { /* leave state unchanged */ } finally {
      setApprovingId(null)
    }
  }

  const copyReply = (key: string, text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(key)
      setTimeout(() => setCopiedId((cur) => (cur === key ? null : cur)), 2000)
    }).catch(() => {})
  }

  const filteredReviews = useMemo(
    () => (filter === "all" ? merged : merged.filter((r) => r.src === filter)).slice(0, 8),
    [merged, filter],
  )

  // Gated / loading / no-intel / managing → the full ReviewsPage owns those states + ingestion.
  if (plan === "host" || data.loading || !hasIntel || managing) {
    return (
      <div className="mx-auto w-full max-w-[1200px] px-4 pt-6 md:px-7">
        {managing && (
          <button
            type="button"
            onClick={() => setManaging(false)}
            className="mb-2 inline-flex items-center gap-1.5 rounded-[9px] border border-border bg-card px-3 py-2 text-[12.5px] font-semibold text-ink-2 transition-colors hover:bg-line-soft"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
            Back to reputation
          </button>
        )}
        <ReviewsPage />
      </div>
    )
  }

  const score = data.intel?.reputation_score ?? 0
  const circumference = 2 * Math.PI * 52
  const dashOffset = circumference * (1 - Math.max(0, Math.min(100, score)) / 100)
  const trend = analysis!.sentiment_trend
  const trendMeta =
    trend === "improving"
      ? { label: "Improving", Icon: TrendingUp, cls: "bg-[rgba(62,224,160,.16)] text-[#9ff0c8]" }
      : trend === "softening"
        ? { label: "Softening", Icon: TrendingDown, cls: "bg-[rgba(217,149,46,.18)] text-[#f4c67a]" }
        : { label: "Steady", Icon: Minus, cls: "bg-white/10 text-[#B9D0C6]" }

  const airbnbRating = data.summary?.average_rating
  const bookingRating = data.bookingSummary?.hotel_rating ?? data.bookingSummary?.computed_average
  const totalReviews =
    data.intel?.review_count ?? (data.summary?.reviews_count || 0) + (data.bookingSummary?.fetched_count || 0)

  // Themes
  const themeMentions = (key: string) => analysis!.themes?.[key]?.mentions ?? 0
  const strengths = (analysis!.strengths || []).slice(0, 4).map((s) => ({
    label: themeDisplayName(s.theme),
    count: themeMentions(s.theme) || s.evidence_count || 0,
  }))
  const risks = (analysis!.risks || []).slice(0, 3).map((r) => ({
    theme: r.theme,
    label: themeDisplayName(r.theme),
    signal: r.signal,
    count: themeMentions(r.theme) || 0,
  }))
  const strengthMax = Math.max(1, ...strengths.map((s) => s.count))
  const riskMax = Math.max(1, ...risks.map((r) => r.count))
  const topRisk = risks[0]

  // Category ratings (Airbnb /5)
  const cats = data.summary?.category_scores
    ? Object.entries(data.summary.category_scores)
        .filter(([, v]) => typeof v === "number" && v != null)
        .map(([k, v]) => ({ label: CATEGORY_LABELS[k] || themeDisplayName(k), value: v as number }))
    : []

  const momentum = momentumSummary(analysis!, analysis!._numeric_context, analysis!._booking_numeric_context, data.seasonProfile)
  const rebooking = analysis!.rebooking_signals?.strength

  const REVIEW_TABS: ("all" | "Airbnb" | "Booking")[] = ["all", "Airbnb", "Booking"]

  return (
    <div className="mx-auto max-w-[1200px] px-4 pb-16 pt-6 md:px-7">
      {/* SCORE HERO */}
      <section
        className="relative mb-[18px] overflow-hidden rounded-card px-[30px] py-[26px] text-[#EAF3EE] shadow-sm"
        style={{ background: "radial-gradient(130% 150% at 0% 0%,#1c4c3b 0%,var(--sf-forest) 60%)" }}
      >
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(52% 74% at 96% 118%,rgba(30,200,148,.2),transparent 70%)" }} />
        <div className="relative flex flex-wrap items-center gap-8">
          <div className="relative h-[124px] w-[124px] flex-none">
            <svg width="124" height="124" viewBox="0 0 124 124">
              <circle cx="62" cy="62" r="52" fill="none" stroke="rgba(255,255,255,.14)" strokeWidth="9" />
              <circle cx="62" cy="62" r="52" fill="none" stroke="#3ee0a0" strokeWidth="9" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={dashOffset} transform="rotate(-90 62 62)" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <strong className="font-display text-[38px] font-bold leading-none">{score}</strong>
              <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-[#9dc2b3]">/ 100</span>
            </div>
          </div>
          <div className="min-w-[240px] flex-1">
            <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-semibold", trendMeta.cls)}>
              <trendMeta.Icon className="h-3 w-3" strokeWidth={2.4} />
              {trendMeta.label}
            </span>
            <h2 className="mt-2.5 font-display text-[26px] font-bold tracking-[-0.02em]">{scoreTitle(score)}</h2>
            <p className="mt-2 max-w-[52ch] text-[14px] font-normal leading-[1.55] text-[#B9D0C6]">
              {analysis!.identity || "Your reputation score blends ratings, sentiment and recency across every channel."}
            </p>
          </div>
          <div className="flex flex-none gap-[26px]">
            {airbnbRating != null && (
              <div>
                <div className="font-display text-[24px] font-bold">{airbnbRating.toFixed(1)}</div>
                <div className="mt-0.5 text-[11.5px] font-medium text-[#9dc2b3]">Airbnb</div>
              </div>
            )}
            {bookingRating != null && (
              <div>
                <div className="font-display text-[24px] font-bold">{bookingRating.toFixed(1)}</div>
                <div className="mt-0.5 text-[11.5px] font-medium text-[#9dc2b3]">Booking /10</div>
              </div>
            )}
            <div>
              <div className="font-display text-[24px] font-bold">{totalReviews}</div>
              <div className="mt-0.5 text-[11.5px] font-medium text-[#9dc2b3]">Reviews</div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY RATINGS + MOMENTUM */}
      <section className="mb-[18px] grid grid-cols-1 gap-[18px] lg:grid-cols-[1fr_1.15fr]">
        <div className="rounded-card border border-border bg-card p-5 shadow-sm">
          <div className="mb-4 flex items-baseline justify-between">
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">Category ratings</span>
            {airbnbRating != null && (
              <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-foreground">
                <Star className="h-[13px] w-[13px] fill-gold text-gold" strokeWidth={0} />
                {airbnbRating.toFixed(1)} avg
              </span>
            )}
          </div>
          {cats.length > 0 ? (
            <div className="flex flex-col gap-[11px]">
              {cats.map((c) => (
                <div key={c.label} className="flex items-center gap-3">
                  <span className="min-w-[112px] text-[13px] font-semibold text-foreground">{c.label}</span>
                  <div className="h-[7px] flex-1 overflow-hidden rounded-full bg-line-soft">
                    <div className="h-full rounded-full bg-brand" style={{ width: `${(c.value / 5) * 100}%` }} />
                  </div>
                  <span className="w-8 text-right font-mono text-[12px] font-semibold text-ink-2">{c.value.toFixed(1)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-6 text-center text-[12.5px] text-muted-foreground">Category ratings appear once Airbnb reviews are synced.</p>
          )}
        </div>
        <div className="rounded-card border border-border bg-card p-5 shadow-sm">
          <div className="mb-3 flex items-baseline justify-between">
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">Momentum</span>
            <span className={cn("inline-flex items-center gap-1 text-[12.5px] font-semibold", trend === "softening" ? "text-amber-strong" : "text-brand-strong")}>
              <trendMeta.Icon className="h-3 w-3" strokeWidth={2.4} />
              {trendMeta.label}
            </span>
          </div>
          <p className="text-[14px] leading-[1.6] text-ink-2">{momentum}</p>
          {rebooking && rebooking !== "none" && (
            <div className="mt-4 flex items-center gap-2 border-t border-line-soft pt-3.5 text-[12.5px] text-muted-foreground">
              <span className="font-semibold text-ink-2">Rebooking signal:</span>
              <span className="rounded-full bg-brand-soft px-2 py-0.5 text-[11px] font-semibold capitalize text-brand-strong">{rebooking}</span>
            </div>
          )}
        </div>
      </section>

      {/* THEMES */}
      <section className="mb-[18px] grid grid-cols-1 gap-[18px] lg:grid-cols-2">
        <div className="rounded-card border border-border bg-card p-5 shadow-sm">
          <div className="mb-3.5 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-brand" />
            <h3 className="font-display text-[15px] font-bold tracking-[-0.01em] text-foreground">Guests love</h3>
          </div>
          <div className="flex flex-col gap-[11px]">
            {strengths.length === 0 && <p className="text-[12.5px] text-muted-foreground">No standout themes yet.</p>}
            {strengths.map((s) => (
              <div key={s.label} className="flex items-center gap-3">
                <span className="min-w-[140px] text-[13px] font-semibold text-foreground">{s.label}</span>
                <div className="h-[7px] flex-1 overflow-hidden rounded-full bg-brand-soft">
                  <div className="h-full bg-brand" style={{ width: `${(s.count / strengthMax) * 100}%` }} />
                </div>
                <span className="font-mono text-[12px] font-semibold text-muted-foreground">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-card border border-border bg-card p-5 shadow-sm">
          <div className="mb-3.5 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-amber" />
            <h3 className="font-display text-[15px] font-bold tracking-[-0.01em] text-foreground">Watch-outs</h3>
          </div>
          {risks.length === 0 ? (
            <p className="text-[12.5px] text-muted-foreground">No recurring watch-outs — clean run.</p>
          ) : (
            <div className="flex flex-col gap-[11px]">
              {risks.map((r) => (
                <div key={r.label} className="flex items-center gap-3">
                  <span className="min-w-[160px] text-[13px] font-semibold text-foreground">{r.label}</span>
                  <div className="h-[7px] flex-1 overflow-hidden rounded-full bg-amber-soft">
                    <div className="h-full bg-amber" style={{ width: `${(r.count / riskMax) * 100}%` }} />
                  </div>
                  <span className="font-mono text-[12px] font-semibold text-muted-foreground">{r.count}</span>
                </div>
              ))}
            </div>
          )}
          {analysis!.sentiment_trend_explanation && (
            <p className="mt-[15px] border-t border-line-soft pt-3 text-[12.5px] leading-[1.5] text-ink-2">
              {analysis!.sentiment_trend_explanation}
            </p>
          )}
        </div>
      </section>

      {/* WATCH-OUT → CREATE TASK (real closed loop into the Tasks backlog) */}
      {topRisk && (() => {
        const taskId = `watchout-${propertyId}-${topRisk.theme}`
        const created = tasks.some((t) => t.id === taskId)
        return (
          <section className="mb-[18px] flex flex-wrap items-center gap-4 rounded-card border border-amber p-[18px_22px] shadow-sm" style={{ background: "linear-gradient(180deg,var(--sf-amber-soft),var(--sf-card) 62%)" }}>
            <div className="min-w-[250px] flex-1">
              <div className="mb-1.5 inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.05em] text-amber-strong">
                <span className="h-[7px] w-[7px] rounded-full bg-amber" />
                Recurring watch-out · {topRisk.label}
              </div>
              <p className="max-w-[64ch] text-[13.5px] leading-[1.5] text-ink-2">
                {topRisk.signal || `${topRisk.count} recent reviews mention ${topRisk.label.toLowerCase()}.`} Stayframe can
                track this as a task and keep it on your backlog until it’s done.
              </p>
            </div>
            <div className="flex flex-none items-center gap-2">
              <button
                type="button"
                disabled={created}
                onClick={() =>
                  addTask({
                    id: taskId,
                    propertyId,
                    propName: property?.name || "Property",
                    source: "Reputation",
                    srcKey: "reputation",
                    urgent: false,
                    title: `Address ${topRisk.label.toLowerCase()} feedback`,
                    detail: topRisk.signal || `${topRisk.count} recent reviews mention ${topRisk.label.toLowerCase()}.`,
                    cta: "View reviews",
                    ctaSurface: "reviews",
                    status: "todo",
                  })
                }
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-[11px] px-[17px] py-[11px] text-[13px] font-semibold transition-opacity",
                  created ? "bg-brand text-white" : "bg-forest text-white hover:opacity-90",
                )}
              >
                {created ? <Check className="h-[15px] w-[15px]" strokeWidth={2.4} /> : <Plus className="h-[15px] w-[15px]" strokeWidth={2.2} />}
                {created ? "Task created" : "Create a task"}
              </button>
              <button
                type="button"
                onClick={() => propertyId && navigate(`/properties/${propertyId}/guest-experience`)}
                className="inline-flex items-center gap-1.5 rounded-[11px] border border-border bg-card px-[15px] py-[11px] text-[13px] font-semibold text-foreground transition-colors hover:bg-line-soft"
              >
                Address this
                <ArrowRight className="h-[15px] w-[15px]" strokeWidth={2.2} />
              </button>
            </div>
          </section>
        )
      })()}

      {/* RECENT REVIEWS */}
      <section className="rounded-card border border-border bg-card p-[22px] shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2.5">
          <h3 className="font-display text-[17px] font-bold tracking-[-0.02em] text-foreground">Recent reviews</h3>
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="flex flex-wrap gap-1.5">
              {REVIEW_TABS.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setFilter(t)}
                  className={cn(
                    "rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition-all",
                    filter === t ? "border-foreground bg-foreground text-white" : "border-border bg-card text-ink-2 hover:border-line-2",
                  )}
                >
                  {t === "all" ? "All reviews" : t}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setManaging(true)}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-[12px] font-semibold text-ink-2 transition-colors hover:bg-line-soft"
            >
              <Settings2 className="h-3.5 w-3.5" strokeWidth={1.9} />
              Manage sources
            </button>
          </div>
        </div>
        {filteredReviews.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <MessageSquare className="h-8 w-8 text-muted-foreground" strokeWidth={1.6} />
            <p className="mt-3 text-[13px] text-muted-foreground">No reviews on this channel yet.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {filteredReviews.map((rv) => (
              <article key={rv.id} className="flex gap-3.5 rounded-2xl border border-border p-4">
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-line-soft font-display text-[15px] font-bold text-ink-2">
                  {rv.initial}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="mb-1.5 flex flex-wrap items-center gap-2.5">
                    <span className="text-[13.5px] font-semibold text-foreground" style={{ fontWeight: 650 }}>{rv.name}</span>
                    <span className="rounded-full px-2 py-0.5 text-[10.5px] font-semibold text-white" style={{ background: rv.srcColor }}>{rv.src}</span>
                    <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-ink-2">
                      <Star className="h-3 w-3 fill-gold text-gold" strokeWidth={0} />
                      {rv.rating.toFixed(1)}{rv.scale === 10 ? "/10" : ""}
                    </span>
                    {rv.ago && <span className="text-[11.5px] font-medium text-muted-foreground">· {rv.ago}</span>}
                  </div>
                  {rv.text && <p className="text-[13px] leading-[1.55] text-ink-2 line-clamp-4">{rv.text}</p>}

                  {/* Reply loop: draft (AI) → edit → approve (human gate) → copy to channel */}
                  {(() => {
                    const key = replyKey(rv)
                    const reply = replies.get(key)
                    const editing = openEditor === key

                    if (editing) {
                      return (
                        <div className="mt-3 rounded-r-[10px] border-l-[3px] border-brand bg-brand-soft px-3.5 py-3">
                          <div className="mb-1.5 inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-brand-strong">
                            <Sparkles className="h-[11px] w-[11px] fill-current" strokeWidth={0} />
                            Suggested reply · your voice
                          </div>
                          <textarea
                            value={editorText}
                            onChange={(e) => setEditorText(e.target.value)}
                            rows={3}
                            className="w-full rounded-[8px] border border-brand/30 bg-white/70 px-2.5 py-2 text-[12.5px] leading-[1.55] text-foreground focus:outline-none focus:ring-2 focus:ring-brand/40"
                          />
                          <div className="mt-2 flex gap-2">
                            <button
                              type="button"
                              onClick={() => approveReply(rv)}
                              disabled={approvingId === key || !editorText.trim()}
                              className="inline-flex items-center gap-1.5 rounded-[8px] bg-forest px-3 py-[7px] text-[12px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                            >
                              {approvingId === key ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.2} /> : <Check className="h-3 w-3" strokeWidth={2.4} />}
                              Approve reply
                            </button>
                            <button
                              type="button"
                              onClick={() => setOpenEditor(null)}
                              className="rounded-[8px] border border-border bg-transparent px-3 py-[7px] text-[12px] font-semibold text-foreground transition-colors hover:bg-white"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )
                    }

                    if (reply?.status === "approved") {
                      return (
                        <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
                          <span className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-brand-strong">
                            <Check className="h-[13px] w-[13px]" strokeWidth={2.2} />
                            Reply approved
                          </span>
                          <button
                            type="button"
                            onClick={() => copyReply(key, reply.text)}
                            className="inline-flex items-center gap-1.5 rounded-[8px] border border-border bg-card px-3 py-1.5 text-[12px] font-semibold text-foreground transition-colors hover:bg-line-soft"
                          >
                            {copiedId === key ? <Check className="h-3 w-3" strokeWidth={2.2} /> : <Copy className="h-3 w-3" strokeWidth={1.9} />}
                            {copiedId === key ? "Copied" : `Copy & post on ${rv.src}`}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setOpenEditor(key); setEditorText(reply.text) }}
                            className="bg-transparent text-[12px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
                          >
                            Edit
                          </button>
                        </div>
                      )
                    }

                    return (
                      <div className="mt-2.5">
                        <button
                          type="button"
                          onClick={() => startDraft(rv)}
                          disabled={draftingId === key}
                          className="inline-flex items-center gap-1.5 rounded-[8px] border border-border bg-card px-3 py-1.5 text-[12px] font-semibold text-foreground transition-colors hover:bg-line-soft disabled:opacity-60"
                        >
                          {draftingId === key ? <Loader2 className="h-[13px] w-[13px] animate-spin" strokeWidth={2} /> : <Sparkles className="h-[13px] w-[13px] fill-current" strokeWidth={0} />}
                          {draftingId === key ? "Drafting…" : "Draft response"}
                        </button>
                      </div>
                    )
                  })()}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
